import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { OnOffSwitch, SegmentedControl, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { TimeGranularityName, TimeGranularitySymbol } from "app/common/utils/timeGranularity";
import {
    getTimeGranularitySymbolByIndex,
    getTimeGranularitySymbolTranslationKey,
    toTimeGranularityName,
    TIME_GRANULARITY_NAMES,
    TIME_GRANULARITY_SYMBOLS,
} from "app/common/utils/timeGranularity";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import {
    MARKET_SHARES_GRAPH_METRIC_TYPE,
    MARKET_SHARES_METRIC_TYPE,
} from "app/components/dashboard/widget-wizard/components/MarketAnalysis/constants";
import DownloadButtonMenu from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import type { IWidgetModelTypesType, IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import { translate } from "app/filters/i18nFilter";
import { getCustomIndustryId } from "app/pages/industry-analysis/overview/widgets/high-level-metrics/single/chartFiltersAndLegends/helpers";
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import DurationService from "app/services/DurationService";
import { getMonthsToDateTooltipText } from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";
import { EChartDisplayType } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";

import { extractPrimaryCategoryFromParams } from "../../routes/routeUtils";

import { EChartType } from "./types";
import { exportChartAsPng } from "./utils/chartExportUtils";
import { dashboardDataDisplayType } from "./utils/utils";

const ButtonsContainer = styled.div`
    margin-right: 12px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    .displayModeSwitcher {
        margin-right: 0;
    }
`;

const DownloadButtonMenuContainer = styled.div`
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;

    & > * {
        flex-shrink: 0;
    }
`;

const offset = {
    x: 50,
    y: 30,
};

/** Serialized into dashboard widget filters for Market Shares graph widgets only. */
const getMarketSharesSaveTimeGranularity = (
    isGraphWidget: boolean,
    timeGranularitySymbol: TimeGranularitySymbol,
): { timeGranularity: TimeGranularityName } | Record<string, never> => {
    if (!isGraphWidget) {
        return {};
    }

    return { timeGranularity: toTimeGranularityName(timeGranularitySymbol) };
};

const resolveTimeGranularityIndex = (timeGranularity: TimeGranularitySymbol): number => {
    const idx = TIME_GRANULARITY_SYMBOLS.indexOf(timeGranularity);
    return idx >= 0 ? idx : 2;
};

interface IMarketSharesActionButtonsProps {
    chartType: EChartType;
    chartDisplayType: EChartDisplayType;
    dashboardKeys: ILegendItems;
    isPeriodOverPeriod: boolean;
    includeRegionalDomains: boolean;
    onSwitcherClick: (displayType: EChartDisplayType) => void;
    setChartType: (chartType: EChartType) => void;
    webSource: IWidgetModelTypesWebSource;
    category: string;
    playersQueryParams: Record<string, any>;
    timeGranularity: TimeGranularitySymbol;
    onTimeGranularityChange: (symbol: TimeGranularitySymbol) => void;
    isDaily: boolean;
    isMonthToDate: boolean;
    onMonthToDateToggle: () => void;
    chartRef: React.RefObject<HTMLDivElement>;
}

export const MarketSharesActionButtons = ({
    chartType,
    chartDisplayType,
    dashboardKeys,
    isPeriodOverPeriod,
    includeRegionalDomains,
    onSwitcherClick,
    setChartType,
    webSource,
    category,
    playersQueryParams,
    timeGranularity,
    onTimeGranularityChange,
    isDaily,
    isMonthToDate,
    onMonthToDateToggle,
    chartRef,
}: IMarketSharesActionButtonsProps) => {
    const { params } = useCurrentStateAndParams();
    const { duration, country } = params;
    const isMTDSupported = DurationService.isMonthToDateSupportedForDuration(duration) && !isDaily;
    const durations = DurationService.getDurationData(duration).forWidget;
    const itemList = [
        {
            title: "%",
            tooltipText: translate("market.players.market.shares.switcher.share.tooltip"),
            displayType: EChartDisplayType.PERCENTAGE,
        },
        {
            title: "#",
            tooltipText: translate("market.players.market.shares.switcher.absolute.tooltip"),
            displayType: EChartDisplayType.ABSOLUTE,
        },
    ];
    const chartTypeOptions: { icon: IconName; disabled?: boolean }[] = [
        {
            icon: "bars",
        },
        {
            icon: "stacked-chart",
            disabled: isPeriodOverPeriod,
        },
    ];

    const timeGranularityIndex = resolveTimeGranularityIndex(timeGranularity);

    const handleTimeGranularitySegmentChange = (index: number) => {
        if (isDaily && index > 1) {
            return;
        }
        onTimeGranularityChange(getTimeGranularitySymbolByIndex(index));
    };

    const timeGranularityOptions = TIME_GRANULARITY_NAMES.map((option) => ({
        label: translate(getTimeGranularitySymbolTranslationKey(option)),
        disabled: chartType === EChartType.AVERAGE || (isDaily && option === "Monthly"),
    }));

    const selectedIndex = itemList.findIndex(({ displayType }) => displayType === chartDisplayType);

    const onItemClick = (selectionIndex) => {
        onSwitcherClick(itemList[selectionIndex].displayType);
    };

    const selectedDashboardKeys = dashboardKeys.filter((key) => key.visible);
    const metricType = chartType === 0 ? MARKET_SHARES_METRIC_TYPE : MARKET_SHARES_GRAPH_METRIC_TYPE;

    const handleChartPngDownload = () => {
        return exportChartAsPng(chartRef, dashboardKeys, offset, "MarketSharesChart", {
            metricTitle: translate("market.players.market.shares.header.title"),
            country,
            durations,
            webSource,
        });
    };

    return (
        <ButtonsContainer>
            <Tooltip
                placement="top"
                content={getMonthsToDateTooltipText(isMTDSupported, isMonthToDate)}
            >
                <Flex alignItems="center">
                    <OnOffSwitch
                        isDisabled={!isMTDSupported}
                        isSelected={isMonthToDate}
                        onClick={onMonthToDateToggle}
                        offLabel={translate("market.players.market.shares.header.mtd.switch")}
                    />
                </Flex>
            </Tooltip>
            <SwitcherGranularityContainer
                itemList={itemList}
                selectedIndex={selectedIndex}
                onItemClick={onItemClick}
                customClass="CircleSwitcher"
                className="displayModeSwitcher"
            />
            <SegmentedControl
                values={chartTypeOptions}
                selectedIndex={chartType}
                onChange={setChartType}
            />
            <SegmentedControl
                values={timeGranularityOptions}
                selectedIndex={timeGranularityIndex}
                defaultSelectedIndex={2}
                onChange={handleTimeGranularitySegmentChange}
            />
            <DownloadButtonMenuContainer>
                <DownloadButtonMenu
                    PNG
                    isDisabled={chartType === EChartType.AVERAGE}
                    handleDownloadFunction={handleChartPngDownload}
                />
            </DownloadButtonMenuContainer>
            {!isPeriodOverPeriod && !includeRegionalDomains ? (
                <AddToDashboard
                    metric={MARKET_SHARES_METRIC_TYPE}
                    type={metricType as IWidgetModelTypesType}
                    webSource={webSource}
                    modelType="fromWebsite"
                    filters={{
                        category: extractPrimaryCategoryFromParams(category),
                        keys: category,
                        chartDisplayType,
                        ...playersQueryParams,
                        ...getMarketSharesSaveTimeGranularity(
                            metricType === MARKET_SHARES_GRAPH_METRIC_TYPE,
                            timeGranularity,
                        ),
                    }}
                    overrideAddToDashboardParams={{
                        key: selectedDashboardKeys,
                        customIndustryId: getCustomIndustryId(category),
                        dataMode: dashboardDataDisplayType[chartDisplayType],
                    }}
                />
            ) : null}
        </ButtonsContainer>
    );
};
